import { useEffect } from "react";
import { SITE_ORIGIN } from "@/lib/siteOrigin";

export interface SeoConfig {
  title: string;
  description: string;
  /** Absolute or path-only URL; resolved against window.location.origin if relative. */
  canonicalPath?: string | undefined;
  /** Absolute URL preferred (Open Graph requires absolute). */
  ogImage?: string | undefined;
  /** If set, injects <link rel="preload" as="image" fetchpriority="high"> to speed up LCP. */
  preloadImage?: string | undefined;
  ogType?: "website" | "article" | "product" | undefined;
  /** BCP 47 language tag, e.g. "en", "pl-PL", "be". */
  locale?: string | undefined;
  /** Optional alternate language URLs for hreflang tags. */
  alternates?: Array<{ hrefLang: string; path: string }> | undefined;
  /** Optional JSON-LD structured data object (will be JSON.stringify'd). */
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>> | undefined;
  /** When true, prevent indexing; public pages are explicitly indexable. */
  noindex?: boolean | undefined;
  /** ISO date string — emitted as article:published_time / og date signals. */
  publishedTime?: string | undefined;
  /** ISO date string — emitted as article:modified_time + lastmod signals. */
  modifiedTime?: string | undefined;
}

const ensureMeta = (
  selector: string,
  attr: "name" | "property",
  attrValue: string,
): HTMLMetaElement => {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, attrValue);
    document.head.appendChild(el);
  }
  return el;
};

const ensureLink = (rel: string, hrefLang?: string): HTMLLinkElement => {
  const selector = hrefLang
    ? `link[rel="${rel}"][hreflang="${hrefLang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hrefLang) el.setAttribute("hreflang", hrefLang);
    document.head.appendChild(el);
  }
  return el;
};

const resolveAbsolute = (path?: string): string | undefined => {
  if (!path) return undefined;
  
  try {
    return new URL(path, SITE_ORIGIN).toString();
  } catch {
    return path;
  }
};

const HREFLANG_MANAGED_FLAG = "data-seo-managed";

/**
 * Centralised SEO head-tag manager. Sets title, description, canonical,
 * Open Graph, Twitter card, hreflang alternates and (optionally) JSON-LD.
 * All updates are reactive to the language / route.
 */
export const useSeo = (config: SeoConfig): void => {
  const {
    title,
    description,
    canonicalPath,
    ogImage,
    preloadImage,
    ogType = "website",
    locale,
    alternates,
    jsonLd,
    publishedTime,
    modifiedTime,
    noindex,
  } = config;

  useEffect(() => {
    if (typeof document === "undefined") return;

    document.title = title;
    if (locale) document.documentElement.setAttribute("lang", locale.split("-")[0]!);

    const canonicalUrl = resolveAbsolute(canonicalPath);

    // Standard meta
    ensureMeta('meta[name="robots"]', "name", "robots").setAttribute(
      "content",
      noindex ? "noindex, nofollow" : "index, follow",
    );
    ensureMeta('meta[name="description"]', "name", "description").setAttribute(
      "content",
      description,
    );

    // Open Graph
    ensureMeta('meta[property="og:title"]', "property", "og:title").setAttribute(
      "content",
      title,
    );
    ensureMeta(
      'meta[property="og:description"]',
      "property",
      "og:description",
    ).setAttribute("content", description);
    ensureMeta('meta[property="og:type"]', "property", "og:type").setAttribute(
      "content",
      ogType,
    );
    if (canonicalUrl) {
      ensureMeta('meta[property="og:url"]', "property", "og:url").setAttribute(
        "content",
        canonicalUrl,
      );
    }
    if (ogImage) {
      ensureMeta('meta[property="og:image"]', "property", "og:image").setAttribute(
        "content",
        ogImage,
      );
    }
    if (locale) {
      ensureMeta('meta[property="og:locale"]', "property", "og:locale").setAttribute(
        "content",
        locale.replace("-", "_"),
      );
    }

    // Twitter
    ensureMeta(
      'meta[name="twitter:card"]',
      "name",
      "twitter:card",
    ).setAttribute("content", "summary_large_image");
    ensureMeta(
      'meta[name="twitter:title"]',
      "name",
      "twitter:title",
    ).setAttribute("content", title);
    ensureMeta(
      'meta[name="twitter:description"]',
      "name",
      "twitter:description",
    ).setAttribute("content", description);
    if (ogImage) {
      ensureMeta(
        'meta[name="twitter:image"]',
        "name",
        "twitter:image",
      ).setAttribute("content", ogImage);
    }

    // Freshness signals (AI/answer engines weigh these heavily)
    if (publishedTime) {
      ensureMeta(
        'meta[property="article:published_time"]',
        "property",
        "article:published_time",
      ).setAttribute("content", publishedTime);
    }
    if (modifiedTime) {
      ensureMeta(
        'meta[property="article:modified_time"]',
        "property",
        "article:modified_time",
      ).setAttribute("content", modifiedTime);
      ensureMeta(
        'meta[property="og:updated_time"]',
        "property",
        "og:updated_time",
      ).setAttribute("content", modifiedTime);
      ensureMeta('meta[name="last-modified"]', "name", "last-modified").setAttribute(
        "content",
        modifiedTime,
      );
    }

    // Canonical
    if (canonicalUrl) {
      ensureLink("canonical").setAttribute("href", canonicalUrl);
    }

    // LCP preload — remove any previously-managed preload, then add current
    document.head
      .querySelectorAll<HTMLLinkElement>('link[rel="preload"][data-seo-managed="1"]')
      .forEach((el) => el.remove());
    if (preloadImage) {
      const link = document.createElement("link");
      link.rel = "preload";
      link.as = "image";
      link.href = preloadImage;
      link.setAttribute("fetchpriority", "high");
      link.setAttribute("data-seo-managed", "1");
      document.head.appendChild(link);
    }

    // hreflang alternates: clear previously-managed entries first
    document.head
      .querySelectorAll<HTMLLinkElement>(
        `link[rel="alternate"][${HREFLANG_MANAGED_FLAG}="1"]`,
      )
      .forEach((el) => el.remove());

    if (alternates && alternates.length > 0) {
      for (const alt of alternates) {
        const link = ensureLink("alternate", alt.hrefLang);
        link.setAttribute("href", resolveAbsolute(alt.path) ?? alt.path);
        link.setAttribute(HREFLANG_MANAGED_FLAG, "1");
      }
    }

    // JSON-LD
    const existing = document.head.querySelector<HTMLScriptElement>(
      'script[type="application/ld+json"][data-seo-managed="1"]',
    );
    if (existing) existing.remove();

    if (jsonLd) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo-managed", "1");
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [
    title,
    description,
    canonicalPath,
    ogImage,
    preloadImage,
    ogType,
    locale,
    alternates,
    jsonLd,
    publishedTime,
    modifiedTime,
    noindex,
  ]);
};
